import { useEffect, useRef } from 'react';
import Lenis from 'lenis';

/**
 * Custom hook for initializing and managing Lenis smooth scroll functionality.
 *
 * @description This hook creates a Lenis instance with optimized configuration for smooth scrolling,
 * exposes it globally for utility functions, and handles proper cleanup on unmount.
 *
 * @returns {Lenis | null} The Lenis instance or null if not yet initialized
 *
 * @example
 * ```tsx
 * function App() {
 *   // Initialize Lenis smooth scroll
 *   useLenis();
 *
 *   return <div>Your content</div>;
 * }
 * ```
 *
 * @see {@link https://github.com/darkroomengineering/lenis} Lenis Documentation
 */
export const useLenis = () => {
  const lenisRef = useRef<Lenis | null>(null);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    // Evitar doble inicialización en React.StrictMode (solo en desarrollo)
    if (window.lenis) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[Lenis] Ya existe una instancia activa (StrictMode doble render)',
        );
      }
      lenisRef.current = window.lenis;
      return () => {
        // No destruimos aquí porque no fuimos quienes la creamos
      };
    }

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      lerp: 0.1,
      wheelMultiplier: 1,
      touchMultiplier: 2,
    });

    lenisRef.current = lenis;
    window.lenis = lenis;

    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 Lenis inicializado con configuración:', {
        duration: 1.2,
        lerp: 0.1,
        easing: 'exponencial suave',
      });
    }

    const raf = (time: number) => {
      lenis.raf(time);
      rafIdRef.current = requestAnimationFrame(raf);
    };
    rafIdRef.current = requestAnimationFrame(raf);

    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      lenis.destroy();
      if (window.lenis === lenis) {
        window.lenis = undefined;
      }
    };
  }, []);

  return lenisRef.current;
};
